import { HttpErrorResponse } from '@angular/common/http';
import { LEAD_TYPE_LABELS, LeadCatalogItem, LeadDetail, LeadPhoneCheck, LeadType } from '#interfaces/lead/lead.interface';
import { LeadService } from '#services/lead/lead.service';
import { AlertService } from '#utils/alert-service/alert-service.service';
import { ParsedWhatsAppLead, parseWhatsAppText } from '#utils/lead/whatsapp-parse';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';
import { Subject, catchError, debounceTime, distinctUntilChanged, filter, of, switchMap, takeUntil } from 'rxjs';

export interface QuickLeadDialogData {
  phone?: string | null;
  contactName?: string | null;
  note?: string | null;
  leadType?: LeadType | null;
}

/**
 * Alta rápida de lead: teléfono + tipo. Mientras se escribe el teléfono avisa si ya hay un lead
 * abierto o un cliente con ese número, para no duplicar.
 */
@Component({
  selector: 'quick-lead-dialog',
  templateUrl: './quick-lead-dialog.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule, MatDialogModule, MatButtonModule, MatButtonToggleModule, MatFormFieldModule,
    MatIconModule, MatInputModule, MatProgressSpinnerModule, MatSelectModule,
  ],
})
export class QuickLeadDialogComponent implements OnInit, OnDestroy {
  data: QuickLeadDialogData | null = inject(MAT_DIALOG_DATA);
  private _dialogRef: MatDialogRef<QuickLeadDialogComponent, LeadDetail> = inject(MatDialogRef);
  private _fb = inject(FormBuilder);
  private _leadService = inject(LeadService);
  private _alertService = inject(AlertService);
  private _router = inject(Router);
  private _cdr = inject(ChangeDetectorRef);

  readonly LeadType = LeadType;
  readonly typeLabels = LEAD_TYPE_LABELS;

  form = this._fb.group({
    phone: [this.data?.phone ?? '', [Validators.required, Validators.minLength(8)]],
    leadType: [this.data?.leadType ?? null as LeadType | null, Validators.required],
    contactName: [this.data?.contactName ?? '', Validators.maxLength(150)],
    typeOriginId: [null as number | null],
    note: [this.data?.note ?? '', Validators.maxLength(600)],
  });

  origins: LeadCatalogItem[] = [];
  phoneCheck: LeadPhoneCheck | null = null;
  checkingPhone = false;
  saving = false;
  /** Lead existente devuelto por el API al intentar crear un duplicado. */
  existingLead: LeadDetail | null = null;
  /** Contact Picker API (Chrome en Android): elegir un contacto de la agenda del teléfono. */
  readonly canPickContact = typeof navigator !== 'undefined' && 'contacts' in navigator && 'ContactsManager' in window;
  readonly canReadClipboard = typeof navigator !== 'undefined' && !!navigator.clipboard?.readText;
  /** Aviso de qué se llenó al pegar desde WhatsApp. */
  pasteHint: string | null = null;

  private _unsubscribeAll = new Subject<void>();


  ngOnInit(): void {
    this._leadService.getOrigins().pipe(takeUntil(this._unsubscribeAll)).subscribe(origins => {
      this.origins = origins;
      if (this.form.controls.typeOriginId.value === null) {
        const whatsapp = origins.find(o => o.name.toLowerCase() === 'whatsapp');
        if (whatsapp) this.form.controls.typeOriginId.setValue(whatsapp.id);
      }
      this._cdr.markForCheck();
    });

    this.form.controls.phone.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      filter(value => {
        this.existingLead = null;
        const enough = (value ?? '').replace(/\D/g, '').length >= 8;
        if (!enough) { this.phoneCheck = null; this._cdr.markForCheck(); }
        return enough;
      }),
      switchMap(value => {
        this.checkingPhone = true;
        this._cdr.markForCheck();
        return this._leadService.checkPhone(value!).pipe(catchError(() => of(null)));
      }),
      takeUntil(this._unsubscribeAll),
    ).subscribe(res => {
      this.checkingPhone = false;
      this.phoneCheck = res?.data ?? null;
      const customer = this.phoneCheck?.customers?.length === 1 ? this.phoneCheck.customers[0] : null;
      if (customer && !this.form.controls.contactName.value) {
        this.form.controls.contactName.setValue(customer.fullName);
      }
      this._cdr.markForCheck();
    });

    if (this.data?.phone) this.form.controls.phone.updateValueAndValidity({ emitEvent: true });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  async pickContact(): Promise<void> {
    try {
      const contacts = await (navigator as unknown as {
        contacts: { select(props: string[], options: { multiple: boolean }): Promise<Array<{ name?: string[]; tel?: string[] }>> };
      }).contacts.select(['name', 'tel'], { multiple: false });
      const contact = contacts?.[0];
      if (!contact) return;
      if (contact.tel?.[0]) this.form.controls.phone.setValue(contact.tel[0]);
      if (contact.name?.[0] && !this.form.controls.contactName.value) this.form.controls.contactName.setValue(contact.name[0]);
      this._cdr.markForCheck();
    } catch {
      // El usuario canceló o el navegador no lo permite.
    }
  }

  /** Lee el portapapeles: mensajes copiados de WhatsApp, un contacto o texto con un teléfono. */
  async pasteFromWhatsApp(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      const parsed = parseWhatsAppText(text);
      if (!parsed || (!parsed.phone && !parsed.name && !parsed.note)) {
        this.pasteHint = 'No encontré datos en lo copiado. En WhatsApp mantén presionados 2 o más mensajes y toca Copiar.';
      } else {
        this._applyParsed(parsed);
      }
    } catch {
      this.pasteHint = 'No se pudo leer el portapapeles. Mantén presionado el campo Nota y pega ahí.';
    }
    this._cdr.markForCheck();
  }

  /** Al pegar una conversación o un contacto en la nota, se reparten los datos en sus campos. */
  onNotePaste(event: ClipboardEvent): void {
    const text = event.clipboardData?.getData('text') ?? '';
    const parsed = parseWhatsAppText(text);
    if (parsed && parsed.source !== 'texto') {
      event.preventDefault();
      this._applyParsed(parsed);
      this._cdr.markForCheck();
    }
  }

  private _applyParsed(parsed: ParsedWhatsAppLead): void {
    const filled: string[] = [];
    if (parsed.phone && !this.form.controls.phone.value?.trim()) {
      this.form.controls.phone.setValue(parsed.phone);
      filled.push('teléfono');
    }
    if (parsed.name && !this.form.controls.contactName.value?.trim()) {
      this.form.controls.contactName.setValue(parsed.name);
      filled.push('nombre');
    }
    const extra = [parsed.company ? `Empresa: ${parsed.company}` : null, parsed.note].filter(Boolean).join('\n');
    if (extra) {
      const current = this.form.controls.note.value?.trim();
      this.form.controls.note.setValue((current ? `${current}\n${extra}` : extra).slice(0, 600));
      filled.push('nota');
    }
    this.pasteHint = filled.length
      ? `Tomé ${filled.join(', ')} de ${parsed.source === 'contacto' ? 'el contacto' : 'la conversación'}.`
      : 'Ya tenías esos datos.';
  }

  setType(type: LeadType): void {
    this.form.controls.leadType.setValue(type);
  }

  openLead(id: number): void {
    this._dialogRef.close();
    this._router.navigate(['/crm/leads', id]);
  }

  save(forceNew: boolean = false): void {
    if (this.form.invalid || this.saving) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.getRawValue();
    this.saving = true;
    this._leadService.quickCreate({
      phone: value.phone!,
      leadType: value.leadType!,
      contactName: value.contactName || null,
      typeOriginId: value.typeOriginId,
      note: value.note || null,
      forceNew,
    }).subscribe({
      next: res => {
        this.saving = false;
        if (res.data.created) {
          this._alertService.saveAlert(res.message ?? 'Lead creado');
          this._dialogRef.close(res.data.lead);
        } else {
          this.existingLead = res.data.lead;
        }
        this._cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this._alertService.errorAlert(err.error?.errors ?? null, err.error?.message ?? 'No se pudo crear el lead');
        this._cdr.markForCheck();
      },
    });
  }
}
